import {
  createContext,
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import type { User } from "@/types/api";
import {
  exchangeCodeForTokens,
  extractUser,
  getLoginUrl,
  getLogoutUrl,
  isTokenExpired,
  refreshTokens,
} from "@/lib/auth";
import { registerTokenGetter } from "@/lib/api";

export interface AuthContextValue {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: () => void;
  loginWithTokens: (idToken: string, accessToken: string, refreshToken: string, expiresIn: number) => void;
  logout: () => void;
  getAccessToken: () => Promise<string | null>;
  handleCallback: (code: string) => Promise<void>;
}

export const AuthContext = createContext<AuthContextValue | null>(null);

const REFRESH_TOKEN_KEY = "prayingmantis_refresh_token";

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const tokensRef = useRef<{
    accessToken: string;
    idToken: string;
    refreshToken: string | null;
  } | null>(null);

  const refreshTimerRef = useRef<ReturnType<typeof setTimeout>>();

  const clearAuth = useCallback(() => {
    tokensRef.current = null;
    setUser(null);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    // Reset the api.ts loop-break counter (#130) so a fast sign-out → sign-in
    // doesn't trip the loop guard on the very first 401 of the new session.
    sessionStorage.removeItem("secops_last_login_redirect");
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
    }
  }, []);

  const scheduleRefresh = useCallback(
    (expiresIn: number) => {
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
      }
      // Refresh 60 seconds before expiry
      const refreshMs = Math.max((expiresIn - 60) * 1000, 10_000);
      refreshTimerRef.current = setTimeout(async () => {
        const rt = tokensRef.current?.refreshToken;
        if (!rt) return;
        try {
          const tokens = await refreshTokens(rt);
          const newRt = tokens.refresh_token ?? rt;
          tokensRef.current = {
            accessToken: tokens.access_token,
            idToken: tokens.id_token,
            refreshToken: newRt,
          };
          localStorage.setItem(REFRESH_TOKEN_KEY, newRt);
          setUser(extractUser(tokens.id_token));
          scheduleRefresh(tokens.expires_in);
        } catch {
          clearAuth();
        }
      }, refreshMs);
    },
    [clearAuth],
  );

  const handleCallback = useCallback(
    async (code: string) => {
      const tokens = await exchangeCodeForTokens(code);
      const rt = tokens.refresh_token ?? null;
      tokensRef.current = {
        accessToken: tokens.access_token,
        idToken: tokens.id_token,
        refreshToken: rt,
      };
      if (rt) localStorage.setItem(REFRESH_TOKEN_KEY, rt);
      setUser(extractUser(tokens.id_token));
      scheduleRefresh(tokens.expires_in);
    },
    [scheduleRefresh],
  );

  const getAccessToken = useCallback(async (): Promise<string | null> => {
    const current = tokensRef.current;
    if (!current) return null;

    // Use ID token for API calls: REST API Cognito authorizer validates `aud` claim
    // which is only present in ID tokens (access tokens use `client_id` instead).
    // ID token also carries custom:tenant_id needed by the Lambda.
    if (!isTokenExpired(current.idToken)) {
      return current.idToken;
    }

    // Try refresh
    if (current.refreshToken) {
      try {
        const tokens = await refreshTokens(current.refreshToken);
        const newRt = tokens.refresh_token ?? current.refreshToken;
        tokensRef.current = {
          accessToken: tokens.access_token,
          idToken: tokens.id_token,
          refreshToken: newRt,
        };
        localStorage.setItem(REFRESH_TOKEN_KEY, newRt);
        setUser(extractUser(tokens.id_token));
        scheduleRefresh(tokens.expires_in);
        return tokens.id_token;
      } catch {
        clearAuth();
        return null;
      }
    }

    clearAuth();
    return null;
  }, [clearAuth, scheduleRefresh]);

  const loginWithTokens = useCallback(
    (idToken: string, accessToken: string, refreshToken: string, expiresIn: number) => {
      tokensRef.current = { accessToken, idToken, refreshToken };
      if (refreshToken) localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
      setUser(extractUser(idToken));
      scheduleRefresh(expiresIn);
    },
    [scheduleRefresh],
  );

  const login = useCallback(async () => {
    window.location.href = await getLoginUrl();
  }, []);

  const logout = useCallback(() => {
    clearAuth();
    window.location.href = getLogoutUrl();
  }, [clearAuth]);

  // Register the token getter for the API client
  useEffect(() => {
    registerTokenGetter(getAccessToken);
  }, [getAccessToken]);

  // On mount, restore session from persisted refresh token
  useEffect(() => {
    let cancelled = false;
    const savedRt = localStorage.getItem(REFRESH_TOKEN_KEY);

    if (savedRt && !tokensRef.current) {
      refreshTokens(savedRt)
        .then((tokens) => {
          if (
            cancelled ||
            tokensRef.current ||
            localStorage.getItem(REFRESH_TOKEN_KEY) !== savedRt
          ) {
            return;
          }

          const newRt = tokens.refresh_token ?? savedRt;
          tokensRef.current = {
            accessToken: tokens.access_token,
            idToken: tokens.id_token,
            refreshToken: newRt,
          };
          localStorage.setItem(REFRESH_TOKEN_KEY, newRt);
          setUser(extractUser(tokens.id_token));
          scheduleRefresh(tokens.expires_in);
        })
        .catch(() => {
          if (
            !cancelled &&
            !tokensRef.current &&
            localStorage.getItem(REFRESH_TOKEN_KEY) === savedRt
          ) {
            localStorage.removeItem(REFRESH_TOKEN_KEY);
          }
        })
        .finally(() => {
          if (!cancelled) setIsLoading(false);
        });
    } else {
      setIsLoading(false);
    }

    return () => {
      cancelled = true;
    };
  }, [scheduleRefresh]);

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: user !== null,
        isLoading,
        login,
        loginWithTokens,
        logout,
        getAccessToken,
        handleCallback,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
